/**
 * The side drawer the Templates and People tabs drill into, without losing the
 * table behind it (its place, sort order and period).
 *
 * Rendered through a portal into document.body rather than in place. `fixed`
 * positioning is only ever fixed to the *viewport* when no ancestor sets a
 * transform — and this page's own entrance animations (animate-card-in,
 * animate-page-in, ...) apply exactly that, which silently turns an ancestor
 * into the drawer's containing block instead. The visible symptom was the
 * drawer scrolling along with the page underneath it, so opening it after
 * scrolling down showed only its lower half. A portal escapes that.
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, X } from 'lucide-react'
import { useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'

export default function DrawerShell({
  open, onClose, onBack = null, ariaLabel, eyebrow, title, subtitle, leading = null, footer = null, children,
}) {
  const panelRef = useRef(null)
  const reduced = usePrefersReducedMotion()
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const iconButton = `p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                      hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                      focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500`

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/70 backdrop-blur-[2px] ${reduced ? '' : 'animate-fade-in'}`}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[440px] bg-white dark:bg-gray-900
                    border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col focus:outline-none
                    ${reduced ? '' : 'animate-drawer-in'}`}
      >
        <header className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          {onBack && (
            <button type="button" onClick={onBack} aria-label="Back" className={`-ml-1.5 mt-0.5 ${iconButton}`}>
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          {leading}
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500">
              {eyebrow}
            </p>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-1 truncate">{title}</h2>
            {subtitle && <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={`ml-auto ${iconButton}`}>
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">{children}</div>

        {footer && (
          <footer className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
            {footer}
          </footer>
        )}
      </aside>
    </>,
    document.body,
  )
}

/** The row of headline figures at the top of a drawer. */
export function DrawerStats({ stats }) {
  return (
    <div className={`grid gap-3 ${stats.length > 3 ? 'grid-cols-2' : 'grid-cols-3'}`}>
      {stats.map(stat => (
        <div key={stat.label} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {stat.label}
          </p>
          <p className="text-[17px] font-bold text-gray-900 dark:text-gray-100 tabular-nums mt-0.5">{stat.value}</p>
        </div>
      ))}
    </div>
  )
}
